import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { invalidateNav } from "./api";
import styles from "./manual.module.css";

import { Button } from "@/components/Button";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { httpClient } from "@/lib/httpClient";
import { slugify } from "@/lib/slugify";

/** Editor affordance on a section landing: create a page, land in the editor.
 * `parents` are the section's top-level pages; the new page can go under one. */
export function NewPageButton({
  sectionId,
  sectionSlug,
  parents = [],
}: {
  sectionId: string;
  sectionSlug: string;
  parents?: Array<{ id: string; title: string }>;
}) {
  const navigate = useNavigate();
  const { csrfToken } = useCurrentUser();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [parentPageId, setParentPageId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setError("Give the page a title first.");
      return;
    }
    const slug = slugify(trimmed);
    if (!slug) {
      setError("The title needs at least one letter or number.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await httpClient.post<{ pageId: string }>("/api/cms/pages", {
        json: { sectionId, slug, title: trimmed, parentPageId: parentPageId || null },
        headers: { "x-csrf-token": csrfToken ?? "" },
      });
      invalidateNav();
      navigate(`/edit/${created.pageId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't create the page");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className={`${styles.editRow} ${styles.newPageRow}`} data-section={sectionSlug}>
        <button type="button" className={styles.editLink} onClick={() => setOpen(true)}>
          + New page
        </button>
      </div>
    );
  }

  return (
    <div className={styles.newPageBox} data-section={sectionSlug}>
      <label className={styles.newPageLabel} htmlFor="new-page-title">
        Title for the new page — numbering comes from the document code, so leave it out.
      </label>
      <input
        id="new-page-title"
        type="text"
        value={title}
        placeholder="e.g. Project kick-off"
        className={error ? `${styles.newPageInput} ${styles.newPageInvalid}` : styles.newPageInput}
        aria-describedby={error ? "new-page-error" : undefined}
        autoFocus
        onChange={(e) => {
          setTitle(e.target.value);
          setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") void create();
          if (e.key === "Escape") setOpen(false);
        }}
      />
      {error && (
        <p id="new-page-error" className={styles.newPageError}>
          {error}
        </p>
      )}
      {parents.length > 0 && (
        <>
          <label className={styles.newPageLabel} htmlFor="new-page-parent">
            Put the new page inside
          </label>
          <select
            id="new-page-parent"
            value={parentPageId}
            className={styles.newPageInput}
            onChange={(e) => setParentPageId(e.target.value)}
          >
            <option value="">No page (top level of the section)</option>
            {parents.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        </>
      )}
      <div className={styles.newPageActions}>
        <Button variant="primary" onClick={() => void create()} disabled={busy}>
          Create page
        </Button>
        <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
